<template>
  <div id="edit-recording-modal" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-md modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body p-5">
          <div class="import-dialog">
            <header>
              <h5 class="mb-3">
                <strong>{{ $t('edit_recording_details.edit_recording_details') }}</strong>
              </h5>
            </header>

            <!-- Email verification banner -->
            <div v-if="!emailVerified" class="d-flex align-items-center gap-3 p-3 rounded mb-3" style="background:#1f2026;border:1px solid rgba(253,223,0,.25)">
              <span class="material-icons text-yellow" style="font-size:20px">mail_outline</span>
              <div class="flex-grow-1">
                <p class="mb-0"><strong>{{ $t('edit_recording_details.verify_your_email') }}</strong></p>
                <p class="mb-0 text-muted"><small>{{ $t('edit_recording_details.check_your_inbox_to_verify_your') }}</small></p>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-warning flex-shrink-0"
                :disabled="resendingEmail"
                @click="resendVerificationEmail"
              >
                {{ resendEmailLabel }}
              </button>
            </div>
            <OpenMailboxButtons v-if="!emailVerified" class="mb-3" />

            <!-- MAIN FORM -->
            <form>
              <!-- Title -->
              <div class="form-group mb-3">
                <label class="mb-2 text-muted">
                  <small>{{ $t('shared.title') }}</small>
                </label>
                <input
                  type="text"
                  class="form-control"
                  v-model="title"
                  :placeholder="$t('edit_recording_details.name_this_recording')"
                />
              </div>

              <!-- Body/Description -->
              <div class="form-group mb-3">
                <label class="mb-2 text-muted">
                  <small>{{ $t('edit_recording_details.add_a_description') }}</small>
                </label>
                <textarea
                  class="form-control"
                  v-model="body"
                  rows="3"
                  :placeholder="$t('edit_recording_details.enter_description')"
                ></textarea>
              </div>

              <!-- Date/Time -->
              <div class="form-group mb-3">
                <label class="mb-2 text-muted" for="date"><small>{{ $t('edit_recording_details.change_date') }}</small></label>
                <input
                  type="datetime-local"
                  class="form-control"
                  v-model="dateTime"
                />
              </div>

              <hr class="my-3">

              <!-- Privacy -->
              <div class="form-group mb-3">
                <p><label class="mb-1"><small><strong>{{ $t('shared.who_can_view') }}</strong></small></label></p>
                <div class="input-group text-muted">
                  <p class="p-2 bg-dark w-100 rounded-pill">
                    <select
                      class="custom-select text-muted w-100"
                      id="recording-privacy-select"
                      :value="isPublic ? '1' : '2'"
                      @change="privacyChanged"
                      :disabled="!emailVerified"
                    >
                      <template v-if="emailVerified">
                        <option value="1">{{ $t('shared.everyone_can_view') }}</option>
                        <option value="2">{{ $t('shared.only_i_can_view') }}</option>
                      </template>
                      <template v-else>
                        <option value="2">{{ $t('shared.only_i_can_view') }}</option>
                      </template>
                    </select>
                  </p>
                </div>
                <div v-if="!emailVerified" class="card p-2 mt-2">
                  <small class="d-block">
                    {{ $t('edit_recording_details.you_must_confirm_your_email_to') }}
                  </small>
                  <button
                    class="btn btn-link p-0"
                    type="button"
                    @click.prevent="resendVerificationEmail"
                    :disabled="resendingEmail"
                  >
                    <small>{{ resendEmailLabel }}</small>
                  </button>
                </div>
              </div>

              <hr class="my-3">

              <!-- SELECT HIKE SECTION -->
              <div class="form-group mb-3">
                <label class="mb-2 text-muted">
                  <small>{{ $t('edit_recording_details.select_an_associated_hike_optional') }}</small>
                </label>

                <!-- Input for searching -->
                <div class="d-flex mb-2">
                  <input
                    type="text"
                    class="form-control"
                    v-model="searchQuery"
                    :placeholder="$t('edit_recording_details.type_a_hike_name')"
                  />
                  <button
                    type="button"
                    class="btn btn-secondary ml-2"
                    @click="searchHikes"
                  >
                    {{ $t('shared.search') }}
                  </button>
                </div>

                <!-- Display search results -->
                <div class="mt-2" v-if="searchResults.length">
                  <div v-for="(trail, index) in searchResults" :key="trail.document.id">
                    <p style="cursor: pointer;" @click="selectTrail(trail)">
                      {{ trail.document.name }}
                      <span class="text-muted" style="font-size: 0.85em;">
                        ({{ trail.document.short_address }})
                      </span>
                    </p>
                  </div>
                </div>

                <!-- Show the currently-selected trail, if any -->
                <div v-if="selectedTrail" class="mt-3 form-group mb-3">
                  <div class="d-flex justify-content-between align-items-center form-control">
                    <div>
                      {{ selectedTrail.document.name }}
                    </div>
                    <!-- Button to unselect/clear the trail -->
                    <button type="button" class="btn btn-sm btn-light d-flex align-items-center" @click="clearSelectedTrail">
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                </div>
              </div>

              <hr class="my-3">

              <!-- ATTACH NEW IMAGES -->
              <div class="form-group mb-3">
                <div class="w-100 d-flex justify-content-between">
                  <label class="mb-2 text-muted">
                    <small>{{ $t('edit_recording_details.attach_new_images') }}</small>
                  </label>
                  <input
                    id="recording-images"
                    type="file"
                    multiple
                    class="form-control-file"
                    accept="image/png, image/jpeg"
                    @change="fileInputEvent"
                    :disabled="uploading"
                  />
                </div>

                <!-- Progress bars & previews -->
                <div
                  v-for="file in newUploads"
                  :key="file.signedId || file.name"
                  class="upload-progress"
                >
                  <div>
                    {{ file.name }}
                    <small> - {{ Math.round(file.progress) }}%</small>
                  </div>
                  <div class="progress-bar-background">
                    <div
                      class="progress-bar-foreground"
                      :style="{ width: file.progress + '%' }"
                    ></div>
                  </div>

                  <!-- SHOW THUMBNAIL PREVIEW -->
                  <img
                    v-if="file.previewUrl"
                    :src="file.previewUrl"
                    class="image-thumbnail mt-2"
                  />
                </div>
              </div>

              <!-- EXISTING IMAGES -->
              <div v-if="existingImages.length" class="form-group mb-3">
                <div class="row">
                  <div class="col-3 d-flex align-items-center position-relative" v-for="(img, index) in existingImages" :key="img.id">
                    <img v-if="img.url" :src="img.url" class="img-fluid square rounded"/>
                    <button class="btn btn-sm btn-secondary p-1 text-center position-absolute d-flex align-items-center" type="button" @click="removeExistingImage(index)" style="top: .5em; right: 1.5em;">
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="d-flex">
                <!-- ACTION BUTTONS -->
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="updateRecording"
                >
                  {{ $t('edit_recording_details.save_changes') }}
                </button>
                <button
                  type="button"
                  class="btn btn-secondary ml-2"
                  data-bs-dismiss="modal"
                >
                  {{ $t('shared.cancel_2') }}
                </button>
              </div>
            </form>
            <!-- END FORM -->
          </div>
        </div>
      </div>
    </div>

    <!-- LOADING DIALOG (Optional) -->
    <LoadingDialog :show-loading-dialog="loading" />
  </div>
</template>

<script lang="ts">
import { nextTick } from 'vue';
import graph_client from '../../utils/graphql/graph_client';
import {GetRecordedTrailById, UpdateRecording, GenerateDirectUpload} from '../../utils/graphql/recorded-trail-graph';
import LoadingDialog from '../../utils/shared_components/LoadingDialog.vue';
import OpenMailboxButtons from '../../utils/shared_components/OpenMailboxButtons.vue';

// Active Storage direct upload (same path the iOS/Android apps + web comments use)
import SparkMD5 from 'spark-md5';
import { hexDigestToBase64 } from '../../utils/hexDigestToBase64';

// Your Typesense class
import {TypesenseResponse} from '../../utils/models/TypesenseResponse';
import { Modal } from 'bootstrap';
import { buildImageAttributesWithCover, KeptImage, DeletedImage } from '../../utils/buildImageAttributesWithCover';
import { validateImageUploadBatch } from '../../utils/validateImageUploadBatch';

export default {
  name: 'EditRecordingModal',
  components: { LoadingDialog, OpenMailboxButtons },
  data() {
    return {
      // Recording fields
      title: '',
      body: '',
      dateTime: null,
      recordingId: null,
      recordingSlug: null,
      loading: false,
      recordingClientId: null,
      isSaving: false,

      // Existing images loaded from the server
      existingImages: [],
      // Images user removed
      imagesMarkedForDeletion: [],
      // New images
      newUploads: [],
      uploading: false,

      // For trail searching
      searchQuery: '',
      searchResults: [], // array of { document: {...}, ... }
      selectedTrail: null, // store the selected "hit" object

      // For the user's review (if a trail is selected)
      reviewId: null,       // if there's an existing review
      reviewRating: 0,      // integer 1..5
      reviewBody: '',       // text

      // Privacy flag
      isPublic: true,

      // Email verification
      emailVerified: !!(window.user && window.user.confirmed_at),
      resendingEmail: false,
      resendEmailLabel: this.$t('edit_recording_details.resend_email'),
    };
  },
  mounted() {
    // Use this.$el to get THIS component's root element, not any other instance
    const element = this.$el;

    console.log('EditRecordingDetails mounted, element ID:', element.id);

    if (!element) {
      console.error('EditRecordingDetails: Modal element not found!');
      return;
    }

    element.addEventListener('hidden.bs.modal', () => {
      this.resetValues();
    });

    // Store reference to this component instance
    const componentInstance = this;

    // Always overwrite the global function to point to THIS instance
    window.showEditRecordingModal = async (recordingSlug) => {
      console.log('showEditRecordingModal called with slug:', recordingSlug);

      // Don't open modal if we're currently saving
      if (componentInstance.isSaving) {
        console.log('Ignoring modal open request - currently saving');
        return;
      }

      console.log('Using component instance with element:', element.id);

      // Reset values first to avoid showing stale data
      componentInstance.resetValues();

      componentInstance.loading = true;
      componentInstance.recordingSlug = recordingSlug;
      await componentInstance.getRecordingDetails();

      // Wait for Vue to update the DOM with the new data
      await nextTick();

      console.log('Modal data loaded - Title:', componentInstance.title, 'Body:', componentInstance.body);

      // Show modal AFTER data is loaded and DOM is updated
      const modal = Modal.getOrCreateInstance(element);
      modal.show();

      // Force another nextTick after modal is shown
      await nextTick();
      console.log('After modal shown - Title input in THIS element:', element.querySelector('input[type="text"]')?.value);
    };
  },
  methods: {
    /**
     * Fetch existing recording details, including any existing review
     */
    async getRecordingDetails() {
      try {
        console.log('Fetching recording with slug:', this.recordingSlug);
        const data = await graph_client.request(GetRecordedTrailById, { slug: `${this.recordingSlug}` });
        console.log('GraphQL response:', data);
        const recording = data.recordingBySlug;

        if (!recording) {
          console.error('No recording found in response');
          alert(this.$t('edit_recording_details.recording_not_found'));
          this.loading = false;
          return;
        }

        console.log('Recording data:', recording);
        console.log('Recording title:', recording.title);
        console.log('Recording body:', recording.body);

        this.recordingId = recording.id;
        this.recordingClientId = recording.clientId;

        // Basic fields - Force reactivity by using $set or direct assignment
        this.title = recording.title || '';
        this.body = recording.body || '';
        this.isPublic = this.emailVerified ? (recording.public ?? true) : false;

        console.log('After assignment - this.title:', this.title);
        console.log('After assignment - this.body:', this.body);
        console.log('After assignment - this.isPublic:', this.isPublic);

        // Force Vue to recognize the changes
        this.$forceUpdate();

        // Date/time
        const date = new Date(recording.createdAt);
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        const hours = ('0' + date.getHours()).slice(-2);
        const minutes = ('0' + date.getMinutes()).slice(-2);
        this.dateTime = `${year}-${month}-${day}T${hours}:${minutes}`;

        // Existing images
        this.existingImages = (recording.images || []).map((img) => ({
          id: img.id,
          url: img.file?.url || '',
          firebaseImageReference: img.firebaseImageReference,
          clientId: img.clientId || this.makeid(12),
          _destroy: false,
        }));

        // If there's an already-attached connected hike
        if (recording.connectedHike) {
          this.selectedTrail = {
            document: {
              id: recording.connectedHike.id,
              name: recording.connectedHike.name,
              short_address: recording.connectedHike.startAddress?.cityCountry || '',
            },
          };
        } else {
          this.selectedTrail = null;
        }

        this.loading = false;
      } catch (error) {
        console.error('Error fetching recording:', error);
        console.error('Error details:', error.response || error.message);
        alert(this.$t('edit_recording_details.failed_to_load_details'));
        this.loading = false;
      }
    },

    /**
     * Remove an existing image from the UI
     */
    removeExistingImage(index) {
      const removedImg = this.existingImages[index];
      this.existingImages.splice(index, 1);
      this.imagesMarkedForDeletion.push({
        id: removedImg.id,
        firebaseImageReference: removedImg.firebaseImageReference,
        clientId: removedImg.clientId || this.makeid(12),
        _destroy: true,
      });
    },

    /**
     * Upload new images to Firebase
     */
    async fileInputEvent(event) {
      const selectedFiles = Array.from(event.target.files);
      if (!selectedFiles.length) return;

      // Validate file types
      const allowedTypes = ['image/jpeg', 'image/png'];
      const validFiles = selectedFiles.filter(file =>
        allowedTypes.includes(file.type)
      );
      if (validFiles.length < selectedFiles.length) {
        alert(this.$t('edit_recording_details.invalid_image_files_ignored'));
      }
      if (!validFiles.length) {
        event.target.value = '';
        return;
      }

      // Enforce per-batch count + per-file size limits (mirrored server-side in
      // GenerateDirectUpload). Reject oversized files and cap how many can be
      // added in one go, accounting for any already queued.
      const { accepted: filesToUpload, error: limitError } =
        validateImageUploadBatch(validFiles, this.newUploads.length);
      if (limitError) {
        alert(limitError);
      }
      if (!filesToUpload.length) {
        event.target.value = '';
        return;
      }

      this.uploading = true;

      // Local preview. Assign a stable clientId per file up front: it travels
      // with every (re)submit of this upload, so a retried updateRecording is
      // de-duped server-side on client_id instead of creating a second row.
      filesToUpload.forEach(file => {
        file.progress = 0;
        file.clientId = this.makeid(12);
        file.previewUrl = URL.createObjectURL(file);
        this.newUploads.push(file);
      });

      // Upload each file via Active Storage direct upload. This replaces the
      // old Firebase Storage path (rails_upload_end_point/web------...), which
      // required the server to download-then-delete the source and produced
      // duplicate file-less rows on retry. We now hand the server a signed_id
      // it attaches directly — the same flow iOS/Android and web comments use.
      for (const file of filesToUpload) {
        try {
          file.progress = 0;

          const checksumBase64 = await this.calculateMD5Base64(file);
          const uploadResponse = await graph_client.request(GenerateDirectUpload, {
            input: {
              filename:       file.name,
              byteSize:       file.size,
              contentType:    file.type || 'image/jpeg',
              checksumBase64: checksumBase64,
              serviceName:    window.publicBucketService,
            },
          });

          const { uploadUrl, headers, signedId } = uploadResponse.generateDirectUpload;
          const response = await fetch(uploadUrl, {
            method:  'PUT',
            headers: {
              ...JSON.parse(headers),
              'Content-Type': file.type || 'image/jpeg',
            },
            body: file,
          });

          if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
          }

          file.signedId = signedId;
          file.progress = 100;
        } catch (error) {
          console.error('Error uploading file:', file.name, error);
        }
      }

      this.uploading = false;
      event.target.value = '';
    },

    /**
     * Compute the base64-encoded MD5 of a file, which Active Storage requires
     * in the direct-upload checksum so it can verify the uploaded bytes.
     */
    calculateMD5Base64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const spark = new SparkMD5.ArrayBuffer();
          spark.append(e.target.result);
          resolve(hexDigestToBase64(spark.end()));
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },

    /**
     * SEARCH HIKES
     */
    async searchHikes() {
      this.searchResults = [];
      try {
        const response = await TypesenseResponse.get(this.searchQuery, null, null, 1, 10);
        if (response && response.hits) {
          this.searchResults = response.hits;
        }
      } catch (error) {
        console.error('Error searching hikes:', error);
      }
    },

    /**
     * Called when the user clicks a hike in the search results
     */
    selectTrail(trail) {
      this.selectedTrail = trail;
      this.searchResults = [];
    },

    makeid(length) {
      let result = '';
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const charactersLength = characters.length;
      let counter = 0;
      while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
      }
      return result;
    },

    privacyChanged(event) {
      if (!this.emailVerified) {
        this.isPublic = false;
        return;
      }
      this.isPublic = event.target.value === '1';
    },

    clearSelectedTrail() {
      this.selectedTrail = null;
    },

    async resendVerificationEmail() {
      this.resendingEmail = true;
      this.resendEmailLabel = this.$t('edit_recording_details.sending');
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        await fetch('/user/resend_confirmation', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Accept': 'application/json',
          },
        });
        this.resendEmailLabel = this.$t('edit_recording_details.sent_check_spam');
      } catch {
        this.resendEmailLabel = this.$t('edit_recording_details.resend_email');
        this.resendingEmail = false;
      }
    },

    /**
     * Update star rating
     */
    setReviewRating(star) {
      this.reviewRating = star;
    },

    /**
     * Build & send the GraphQL update
     */
    updateRecording() {
      this.loading = true;
      const formattedDate = new Date(this.dateTime).toISOString();

      // Order the kept images so the helper can flag the first one as the
      // cover photo: existing images first (in their current display order),
      // then any successfully uploaded new images. Deletions are appended
      // separately with _destroy=true and never carry the cover flag.
      const kept: KeptImage[] = [
        ...this.existingImages.map((img: any) => ({
          kind: 'existing' as const,
          id: img.id,
          clientId: img.clientId,
        })),
        ...this.newUploads
          .filter((f: any) => f.signedId)
          .map((f: any) => ({
            kind: 'new-signed' as const,
            signedId: f.signedId,
            clientId: f.clientId,
          })),
      ];
      const deletions: DeletedImage[] = this.imagesMarkedForDeletion.map((img: any) => ({
        id: img.id,
        firebaseImageReference: img.firebaseImageReference,
        clientId: img.clientId,
      }));

      const imagesAttributes = buildImageAttributesWithCover(kept, deletions, {
        parentClientId: this.recordingClientId,
        generateClientId: () => this.makeid(12),
      });

      // Build the base input object
      const input = {
        id: this.recordingId,
        title: this.title,
        body: this.body,
        createdAt: formattedDate,
        imagesAttributes,
        public: this.isPublic,
      };

      // If user selected a hike, add connected_hike_id
      if (this.selectedTrail) {
        input.connectedHikeId = this.selectedTrail.document.id;
      }

      // Set flag to prevent modal from reopening during save/reload
      this.isSaving = true;

      graph_client
        .request(UpdateRecording, { input })
        .then(() => {
          this.loading = false;

          // Find ALL elements with this ID (there might be duplicates)
          const allModals = document.querySelectorAll('[id="edit-recording-modal"]');
          console.log('Found', allModals.length, 'modal elements');

          allModals.forEach((element, index) => {
            console.log(`Hiding modal ${index + 1}`);

            let modal = Modal.getInstance(element);
            if (modal) {
              modal.dispose();
            }

            // Force hide each modal
            element.style.setProperty('display', 'none', 'important');
            element.classList.remove('show');
            element.setAttribute('aria-hidden', 'true');
          });

          // Remove all backdrops
          const backdrops = document.querySelectorAll('.modal-backdrop');
          console.log('Found', backdrops.length, 'backdrops');
          backdrops.forEach(backdrop => backdrop.remove());

          // Remove modal-open class from body
          document.body.classList.remove('modal-open');
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';

          // Reload the recordings list if on list page, otherwise refresh the show page
          if (window.reloadRecordedTrails) {
            window.reloadRecordedTrails();
            this.isSaving = false;
          } else {
            // Refresh the page to show updated recording details
            window.location.reload();
          }
        })
        .catch((error) => {
          console.error('Error updating recording:', error);
          this.loading = false;
          this.isSaving = false;
        });
    },

    /**
     * Reset all local data
     */
    resetValues() {
      this.title = '';
      this.body = '';
      this.dateTime = null;
      this.recordingId = null;

      this.existingImages = [];
      this.imagesMarkedForDeletion = [];
      this.newUploads = [];

      this.searchQuery = '';
      this.searchResults = [];
      this.selectedTrail = null;

      // Reset review
      this.reviewId = null;
      this.reviewRating = 0;
      this.reviewBody = '';

      this.isPublic = true;
    },
  },
};
</script>

<style scoped>
/* Minimal styling - adapt to your needs */

.existing-images {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.existing-image {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.image-thumbnail {
  max-width: 120px;
  max-height: 100px;
  object-fit: cover;
  margin-bottom: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.upload-progress {
  margin-top: 8px;
}

.progress-bar-background {
  background: #eee;
  width: 100%;
  height: 6px;
  margin-top: 4px;
  position: relative;
  border-radius: 3px;
}

.progress-bar-foreground {
  height: 6px;
  background: #428bca;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 3px;
}

/* Simple star rating styling */
.star-rating {
  display: inline-block;
}
.star {
  font-size: 1.5em;
  color: #ccc;
  cursor: pointer;
  margin-right: 4px;
}
.star.filled {
  color: #fddf00; /* gold */
}

.loading {
  color: #555;
}
</style>
